import React, { useState, useEffect, useRef } from "react";
import { useWebData } from "../context/WebDataContext"; 
import { db } from "../firebaseConfig";
import { collection, query, where, getDocs } from "firebase/firestore";
import { useLocation } from "react-router-dom"; // 🔥 Auto-catch Serial No
import Loader from "../components/Loader";
import * as XLSX from "xlsx";

function AssetHistory() {
  const location = useLocation(); // Data from OrgHistory
  const { installations } = useWebData(); // Context for Suggestions

  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Suggestions
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef(null);

  // Data States
  const [machineList, setMachineList] = useState([]); 
  const [selectedMachine, setSelectedMachine] = useState(null); 
  const [timeline, setTimeline] = useState([]); 
  const [viewItem, setViewItem] = useState(null);

  // ==========================================
  // 🔥 0. AUTO SEARCH (From OrgHistory Link)
  // ==========================================
  useEffect(() => {
      if (location.state && location.state.serialNo) {
          setSearchTerm(location.state.serialNo);
          performSearch(location.state.serialNo);
      }
  }, [location.state]);

  // ==========================================
  // 🔥 1. SMART SUGGESTIONS
  // ==========================================
  useEffect(() => {
      function handleClickOutside(event) {
          if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
              setShowSuggestions(false);
          }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef]);

  const handleInputChange = (e) => {
      const txt = e.target.value;
      setSearchTerm(txt);

      if (txt.length > 1 && installations) {
          // Match Serial No OR Hospital Name
          const matches = installations.filter(i => 
              (i.serialNo && i.serialNo.toLowerCase().includes(txt.toLowerCase())) ||
              (i.hospital && i.hospital.toLowerCase().includes(txt.toLowerCase()))
          ).map(i => `${i.serialNo} - ${i.hospital}`);

          setSuggestions([...new Set(matches)].slice(0, 10));
          setShowSuggestions(true);
      } else {
          setShowSuggestions(false);
      }
  };

  const selectSuggestion = (val) => {
      const serialOnly = val.split(' - ')[0]; // Only take Serial No
      setSearchTerm(serialOnly);
      setShowSuggestions(false);
      setTimeout(() => performSearch(serialOnly), 100); 
  };

  // ==========================================
  // 2. MASTER SEARCH LOGIC
  // ==========================================
  const handleSearch = () => performSearch(searchTerm);

  const performSearch = async (keywordRaw) => {
    if (!keywordRaw) return alert("Enter Serial Number!");
    setLoading(true);
    setMachineList([]);
    setSelectedMachine(null);
    setTimeline([]);

    try {
        const keyword = keywordRaw.trim();
        let foundMachines = [];

        // A. Search in Installations (Primary Source)
        const qSerial = query(collection(db, "installations"), where("serialNo", "==", keyword));
        const snapSerial = await getDocs(qSerial);
        snapSerial.forEach(doc => foundMachines.push(doc.data()));

        // B. Fallback: Search by Hospital (Returns multiple machines)
        if(foundMachines.length === 0) {
            const qHosp = query(collection(db, "installations"), where("hospital", "==", keyword));
            const snapHosp = await getDocs(qHosp);
            snapHosp.forEach(doc => foundMachines.push(doc.data()));
        }

        // C. If still 0, Check Service Records directly (Maybe installation record missing)
        if(foundMachines.length === 0) {
             const qSvc = query(collection(db, "service_reports"), where("serialNo", "==", keyword));
             const snapSvc = await getDocs(qSvc);
             if(!snapSvc.empty) {
                 // Create a dummy machine object from service record
                 foundMachines.push({
                     serialNo: keyword,
                     hospital: snapSvc.docs[0].data().hospitalName || "Unknown",
                     product: "Unknown (No Install Record)",
                     isVirtual: true // Mark as not from installation DB
                 });
             }
        }

        if (foundMachines.length === 0) {
            alert("❌ No records found!");
        } else if (foundMachines.length === 1) {
            // Exact Match found
            loadHistory(foundMachines[0]);
        } else {
            // Multiple Matches (e.g. Hospital Search)
            setMachineList(foundMachines);
        }

    } catch (error) { console.error(error); alert("Search Error"); }
    setLoading(false);
  };

  // ==========================================
  // 🔥 3. LOAD COMPLETE HISTORY (KUNDALI)
  // ==========================================
  const loadHistory = async (machine) => {
      setLoading(true);
      setMachineList([]); 
      setSelectedMachine(`${machine.serialNo} (${machine.hospital})`);

      let history = [];
      const serial = machine.serialNo;

      // 1. Installation Data
      if (machine.date && !machine.isVirtual) {
          history.push({
              id: "install",
              date: machine.date,
              type: "Installation",
              title: `Installed: ${machine.product}`,
              desc: `Warranty: ${machine.warrantyExpiry} | Eng: ${machine.userName || 'Admin'}`,
              details: machine, 
              theme: "blue"
          });
      }

      // 2. Fetch PMS Reports
      const qPMS = query(collection(db, "pms_reports"), where("serialNo", "==", serial));
      const snapPMS = await getDocs(qPMS);
      snapPMS.forEach(doc => {
          const d = doc.data();
          history.push({
              id: doc.id,
              date: d.date || d.createdAt,
              type: "PMS Report",
              title: `Cycle: ${d.currentPmsNumber}/${d.totalPms}`,
              desc: `Status: ${d.status} | By: ${d.userName}`,
              details: d,
              theme: d.status === 'Done' ? "green" : "orange"
          });
      });

      // 3. Fetch Service Reports (Breakdowns)
      const qSvc = query(collection(db, "service_reports"), where("serialNo", "==", serial));
      const snapSvc = await getDocs(qSvc);
      snapSvc.forEach(doc => {
          const d = doc.data();
          history.push({
              id: doc.id,
              date: d.date || d.createdAt,
              type: "Service Call",
              title: `Issue: ${d.issue}`,
              desc: `Action: ${d.action} | Status: ${d.status}`,
              details: d,
              theme: "red"
          });
      });

      // 4. Fetch Service Calls (Tickets - If stored separately)
      const qCalls = query(collection(db, "service_calls"), where("serialNo", "==", serial));
      const snapCalls = await getDocs(qCalls);
      snapCalls.forEach(doc => {
          const d = doc.data();
          history.push({
              id: doc.id,
              date: d.createdAt,
              type: "Ticket Raised",
              title: `Ticket: ${d.remark}`,
              desc: `Status: ${d.status}`,
              details: d,
              theme: "red"
          });
      });

      // 5. Fetch Demos
      const qDemo = query(collection(db, "demo_notes"), where("serialNo", "==", serial));
      const snapDemo = await getDocs(qDemo);
      snapDemo.forEach(doc => {
          const d = doc.data();
          history.push({
              id: doc.id,
              date: d.date,
              type: "Demo",
              title: `Result: ${d.result}`,
              desc: `Person: ${d.contactPerson}`,
              details: d,
              theme: "teal"
          });
      });

      // Sort by Date (Oldest to Newest)
      history.sort((a, b) => new Date(a.date) - new Date(b.date));
      setTimeline(history);
      setLoading(false);
  };

  // ==========================================
  // 4. UI HELPERS
  // ==========================================
  const getTheme = (theme) => {
      switch(theme) {
          case 'blue': return { color: '#2980b9', bg: '#eaf2f8', icon: '⚙️', border: '4px solid #2980b9' };
          case 'red': return { color: '#c0392b', bg: '#f9e79f', icon: '🛠️', border: '4px solid #c0392b' };
          case 'green': return { color: '#27ae60', bg: '#e9f7ef', icon: '✅', border: '4px solid #27ae60' };
          case 'orange': return { color: '#e67e22', bg: '#fef5e7', icon: '⏳', border: '4px solid #e67e22' };
          case 'teal': return { color: '#16a085', bg: '#e8f8f5', icon: '🎭', border: '4px solid #16a085' };
          default: return { color: '#555', bg: '#fff', icon: '📄', border: '4px solid #ccc' };
      }
  };

  const formatDate = (iso) => {
      if(!iso) return "-";
      const d = new Date(iso);
      return isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const downloadExcel = () => {
      const ws = XLSX.utils.json_to_sheet(timeline.map(t => ({
          Date: formatDate(t.date), Type: t.type, Title: t.title, Description: t.desc
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "History");
      XLSX.writeFile(wb, `${searchTerm}_History.xlsx`);
  };

  return (
    <div style={{padding:"20px", fontFamily:"'Segoe UI', sans-serif", maxWidth:"900px", margin:"0 auto"}}>
      
      {/* SEARCH AREA */}
      <div style={searchBoxStyle} ref={wrapperRef}>
          <h2 style={{margin:0, color:"#2c3e50"}}>🔍 Machine History (Kundali)</h2>
          
          <div style={{position: "relative", marginTop:"15px", display:"flex", gap:"10px"}}>
              <div style={{flex: 1, position: "relative"}}>
                  <input 
                    type="text" 
                    placeholder="Search Serial No or Hospital..." 
                    value={searchTerm} 
                    onChange={handleInputChange} 
                    onFocus={() => searchTerm.length > 1 && setShowSuggestions(true)}
                    style={inputStyle}
                  />
                  {/* Suggestions */}
                  {showSuggestions && suggestions.length > 0 && (
                      <ul style={suggestionsBox}>
                          {suggestions.map((s, i) => (
                              <li key={i} onClick={() => selectSuggestion(s)} style={suggestionItem}>
                                  {s.includes('-') ? '🔩' : '🏥'} {s}
                              </li>
                          ))}
                      </ul>
                  )}
              </div>
              <button onClick={handleSearch} style={btnSearch}>Search</button>
          </div>
      </div>

      {loading && <Loader />}

      {/* RESULT SELECTION (If searching by Hospital) */}
      {machineList.length > 0 && (
          <div style={{marginTop:"20px"}}>
              <h3 style={{color:"#555"}}>Select Machine:</h3>
              {machineList.map((m, i) => (
                  <div key={i} onClick={() => loadHistory(m)} style={machineCardStyle}>
                      <strong>{m.product}</strong> <br/>
                      <span style={{color:"#555", fontSize:"13px"}}>{m.serialNo} - {m.hospital}</span>
                  </div>
              ))}
          </div>
      )}

      {/* 🔥 TIMELINE (COMPLETE HISTORY) */}
      {selectedMachine && timeline.length > 0 && (
          <div style={{marginTop:"30px"}}>
              <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", paddingBottom:"15px", borderBottom:"1px solid #ddd"}}>
                  <h3 style={{margin:0, color:"#333"}}>📜 History: {selectedMachine}</h3>
                  <button onClick={downloadExcel} style={btnDownload}>📥 Excel</button>
              </div>
              
              <div style={timelineContainer}>
                  {timeline.map((item, index) => {
                      const theme = getTheme(item.theme);
                      return (
                          <div key={index} style={timelineItem} onClick={() => setViewItem(item)}>
                              {/* Date Bubble */}
                              <div style={{...dateBubble, backgroundColor: theme.color}}>
                                  {formatDate(item.date).split('/')[0]}<br/>
                                  <span style={{fontSize:"10px"}}>{formatDate(item.date).split('/').slice(1).join('/')}</span>
                              </div>
                              
                              {/* Content Card */}
                              <div style={{...contentCard, borderLeft: theme.border, backgroundColor: theme.bg}}>
                                  <div style={{display:"flex", justifyContent:"space-between"}}>
                                      <strong style={{color: theme.color, fontSize:"14px"}}>{theme.icon} {item.type}</strong>
                                      <small style={{color:"#666"}}>{formatDate(item.date)}</small>
                                  </div>
                                  <div style={{fontWeight:"bold", marginTop:"5px", color:"#333"}}>{item.title}</div>
                                  <p style={{margin:"5px 0", fontSize:"12px", color:"#555"}}>{item.desc}</p>
                              </div>
                          </div>
                      );
                  })}
              </div>
          </div>
      )}

      {selectedMachine && timeline.length === 0 && !loading && (
          <div style={{textAlign:"center", color:"gray", marginTop:"50px", padding:"20px", background:"#f9f9f9", borderRadius:"10px"}}>
              <p>🚫 No history found for this machine.</p>
              <small>Try checking if Serial No is correct in reports.</small>
          </div>
      )}

      {/* POPUP MODAL */}
      {viewItem && (
          <div style={modalOverlay}>
              <div style={modalContent}>
                  <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", borderBottom:"1px solid #eee", paddingBottom:"10px"}}>
                      <h3 style={{margin:0, color: getTheme(viewItem.theme).color}}>{viewItem.type} Details</h3>
                      <button onClick={() => setViewItem(null)} style={{background:"none", border:"none", fontSize:"18px", cursor:"pointer"}}>✖</button>
                  </div>
                  
                  <div style={{fontSize:"13px", lineHeight:"1.8", marginTop:"10px"}}>
                      <table style={{width:"100%", borderCollapse:"collapse"}}>
                          <tbody>
                              {Object.entries(viewItem.details).map(([key, value]) => {
                                  if(key === 'id' || key === 'createdAt' || key === 'updatedAt' || typeof value === 'object' || !value) return null;
                                  return (
                                      <tr key={key} style={{borderBottom:"1px solid #f0f0f0"}}>
                                          <td style={{fontWeight:"bold", color:"#555", textTransform:"capitalize", padding:"5px 0", width:"40%"}}>{key.replace(/([A-Z])/g, ' $1')}</td>
                                          <td style={{padding:"5px 0", textAlign:"right", wordBreak:"break-word"}}>{value.toString()}</td>
                                      </tr>
                                  );
                              })}
                          </tbody>
                      </table>
                  </div>
                  <button onClick={() => setViewItem(null)} style={btnClose}>Close</button>
              </div>
          </div>
      )}

    </div>
  );
}

// STYLES
const searchBoxStyle = { textAlign:"center", background:"white", padding:"20px", borderRadius:"12px", boxShadow:"0 4px 15px rgba(0,0,0,0.05)", marginBottom:"20px", position:"relative", zIndex: 10 };
const inputStyle = { padding:"12px", width:"100%", borderRadius:"8px", border:"1px solid #ccc", fontSize:"15px", outline:"none", boxSizing:"border-box" };
const btnSearch = { padding:"12px 25px", backgroundColor:"#2c3e50", color:"white", border:"none", borderRadius:"8px", cursor:"pointer", fontWeight:"bold", fontSize:"14px" };
const btnDownload = { padding:"8px 15px", backgroundColor:"#27ae60", color:"white", border:"none", borderRadius:"5px", cursor:"pointer", fontSize:"12px" };

const machineCardStyle = { padding:"15px", background:"white", marginBottom:"10px", borderRadius:"8px", boxShadow:"0 2px 5px rgba(0,0,0,0.05)", cursor:"pointer", borderLeft:"5px solid #3498db" };

const timelineContainer = { position: "relative", padding: "20px 0", marginLeft: "10px" };
const timelineItem = { position: "relative", marginBottom: "20px", display:"flex", gap:"15px", alignItems:"flex-start" };

const dateBubble = { 
    minWidth: "55px", height: "55px", borderRadius: "50%", 
    color: "white", display: "flex", flexDirection:"column", alignItems: "center", justifyContent: "center", 
    fontSize: "14px", fontWeight:"bold", boxShadow:"0 3px 6px rgba(0,0,0,0.2)",
    zIndex: 2, flexShrink: 0
};

const contentCard = { 
    flex: 1, padding: "15px", borderRadius: "8px", 
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)", cursor: "pointer", transition: "0.2s"
};

const suggestionsBox = {
    position: "absolute", top: "100%", left: 0, width: "100%", background: "white", 
    border: "1px solid #ddd", borderRadius: "8px", boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
    maxHeight: "250px", overflowY: "auto", listStyle: "none", padding: 0, margin: "5px 0", zIndex: 100
};
const suggestionItem = { padding: "12px", borderBottom: "1px solid #f0f0f0", cursor: "pointer", textAlign: "left", fontSize: "14px", transition:"0.1s" };

const modalOverlay = { position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000 };
const modalContent = { background:"white", padding:"25px", borderRadius:"12px", width:"450px", boxShadow:"0 10px 30px rgba(0,0,0,0.3)", maxHeight:"80vh", overflowY:"auto" };
const btnClose = { marginTop:"15px", backgroundColor:"#333", color:"white", border:"none", padding:"10px", borderRadius:"8px", width:"100%", cursor:"pointer" };

export default AssetHistory;